import React from 'react';
import {
  Grid,
  Typography,
  Slider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { GapStylingConfig } from './types';

interface StyleTabProps {
  config: GapStylingConfig;
  onConfigChange: (updates: Partial<GapStylingConfig>) => void;
  onNestedConfigChange: <T extends keyof GapStylingConfig>(
    key: T,
    updates: Partial<GapStylingConfig[T]>
  ) => void;
}

/**
 * Style tab component for gap styling configuration
 * Handles border, box shadow, and blend mode settings
 */
const StyleTab: React.FC<StyleTabProps> = ({
  config,
  onConfigChange,
  onNestedConfigChange
}) => {
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <Typography gutterBottom>Border Radius (px)</Typography>
        <Slider
          value={config.borderRadius}
          onChange={(_, value) => onConfigChange({ borderRadius: value as number })}
          min={0}
          max={50}
          valueLabelDisplay="auto"
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <Typography gutterBottom>Border Width (px)</Typography>
        <Slider
          value={config.borderWidth}
          onChange={(_, value) => onConfigChange({ borderWidth: value as number })}
          min={0}
          max={10}
          valueLabelDisplay="auto"
        />
      </Grid>

      {config.borderWidth > 0 && (
        <>
          <Grid item xs={6}>
            <TextField
              fullWidth
              type="color"
              label="Border Color"
              value={config.borderColor}
              onChange={(e) => onConfigChange({ borderColor: e.target.value })}
            />
          </Grid>

          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Border Style</InputLabel>
              <Select
                value={config.borderStyle}
                onChange={(e) => onConfigChange({ borderStyle: e.target.value as GapStylingConfig['borderStyle'] })}
              >
                <MenuItem value="solid">Solid</MenuItem>
                <MenuItem value="dashed">Dashed</MenuItem>
                <MenuItem value="dotted">Dotted</MenuItem>
                <MenuItem value="double">Double</MenuItem>
                <MenuItem value="groove">Groove</MenuItem>
                <MenuItem value="ridge">Ridge</MenuItem>
                <MenuItem value="inset">Inset</MenuItem>
                <MenuItem value="outset">Outset</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </>
      )}

      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Switch
              checked={config.boxShadow.enabled}
              onChange={(e) => onNestedConfigChange('boxShadow', { enabled: e.target.checked })}
            />
          }
          label="Enable Box Shadow"
        />
      </Grid>

      {config.boxShadow.enabled && (
        <>
          <Grid item xs={3}>
            <TextField
              fullWidth
              type="number"
              label="Shadow X"
              value={config.boxShadow.x}
              onChange={(e) => onNestedConfigChange('boxShadow', { x: Number(e.target.value) })}
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              fullWidth
              type="number"
              label="Shadow Y"
              value={config.boxShadow.y}
              onChange={(e) => onNestedConfigChange('boxShadow', { y: Number(e.target.value) })}
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              fullWidth
              type="number"
              label="Blur"
              value={config.boxShadow.blur}
              onChange={(e) => onNestedConfigChange('boxShadow', { blur: Number(e.target.value) })}
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              fullWidth
              type="number"
              label="Spread"
              value={config.boxShadow.spread}
              onChange={(e) => onNestedConfigChange('boxShadow', { spread: Number(e.target.value) })}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              type="color"
              label="Shadow Color"
              value={config.boxShadow.color.substring(0, 7)}
              onChange={(e) => onNestedConfigChange('boxShadow', { color: e.target.value + '40' })}
            />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={config.boxShadow.inset}
                  onChange={(e) => onNestedConfigChange('boxShadow', { inset: e.target.checked })}
                />
              }
              label="Inset Shadow"
            />
          </Grid>
        </>
      )}

      <Grid item xs={12}>
        <FormControl fullWidth>
          <InputLabel>Blend Mode</InputLabel>
          <Select
            value={config.blendMode}
            onChange={(e) => onConfigChange({ blendMode: e.target.value as GapStylingConfig['blendMode'] })}
          >
            <MenuItem value="normal">Normal</MenuItem>
            <MenuItem value="multiply">Multiply</MenuItem>
            <MenuItem value="screen">Screen</MenuItem>
            <MenuItem value="overlay">Overlay</MenuItem>
            <MenuItem value="darken">Darken</MenuItem>
            <MenuItem value="lighten">Lighten</MenuItem>
            <MenuItem value="color-dodge">Color Dodge</MenuItem>
            <MenuItem value="color-burn">Color Burn</MenuItem>
            <MenuItem value="hard-light">Hard Light</MenuItem>
            <MenuItem value="soft-light">Soft Light</MenuItem>
            <MenuItem value="difference">Difference</MenuItem>
            <MenuItem value="exclusion">Exclusion</MenuItem>
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );
};

export default StyleTab;